import classNames from 'classnames'
import React from 'react'

import Data from 'data'
import Store from 'store'

import { gemItemIcon, gemName } from '@/logic/gem'
import { itemBaseIcon } from '@/logic/item'
import { SkillsEmbed, SkillsEmbedSkill } from '@/types/embed'
import { Gem } from '@/types/item'
import { getIconPath } from 'common'
import { getGemColor } from 'logic/mods'
import { WithGemTooltip, WithItemTemplateTooltip, WithItemTooltip } from 'tooltip'
import Slider from 'ui/slider'
import Tooltip from 'ui/tooltip'
import './SkillGems.scss'

export const skillItemTypes = [
  'Any Item',
  '1-Handed Melee',
  '1-Handed Ranged',
  '2-Handed Melee',
  '2-Handed Ranged',
  'Amulet',
  'Armour',
  'Belt',
  'Boots',
  'Flask',
  'Gloves',
  'Helmet',
  'Quiver',
  'Ring',
  'Shield',
  'Cluster Jewel'
]

function SkillGem({ gem, link }: { gem: Gem; link?: boolean }) {
  const gemData = Data.gems[gem.id]
  if (!gemData) return null
  const icon = gemItemIcon(gemData)
  if (!icon) return null
  return (
    <div className={`poe-gem poe-gem-${getGemColor(gemData)}`}>
      <WithGemTooltip gem={gem}>
        <img src={getIconPath(icon)} alt={gemName(gemData)} />
      </WithGemTooltip>
      {!!link && <div className="poe-link" />}
    </div>
  )
}

const SkillType = ({ type }: { type: number }) => (
  <Tooltip tooltip={skillItemTypes[type]}>
    <div className="poe-item-type" style={{ backgroundPositionX: `-${type}em` }} />
  </Tooltip>
)

function SkillItem({ id }: { id: number | string }) {
  const storeItem = Store.useItem(typeof id === 'number' ? id : 0)
  if (storeItem) {
    const data = (storeItem.unique && Data.uniques[storeItem.unique]) || Data.items[storeItem.base]
    const icon = data ? itemBaseIcon(data) : undefined
    if (icon) {
      return (
        <WithItemTooltip item={storeItem}>
          <div className="poe-item-item">
            <img src={getIconPath(icon)} alt={data.name} />
          </div>
        </WithItemTooltip>
      )
    }
  }
  const baseItem = Data.uniques[id] ? Data.uniques[id] : Data.items[id]
  const icon = baseItem ? itemBaseIcon(baseItem) : undefined
  if (icon) {
    return (
      <WithItemTemplateTooltip name={String(id)}>
        <div className="poe-item-item">
          <img src={getIconPath(icon)} alt={baseItem.name} />
        </div>
      </WithItemTemplateTooltip>
    )
  }
  return <SkillType type={0} />
}

const SkillSlot = ({ type, item, gems, unlink }: SkillsEmbedSkill) => (
  <div className="poe-skill-slot">
    {item != null ? <SkillItem id={item} /> : <SkillType type={type || 0} />}
    <div className="poe-gem-list">
      {gems.map((gem, index) => (
        <SkillGem gem={gem} key={index} link={!unlink?.[index - 1]} />
      ))}
    </div>
  </div>
)

export default function SkillGems({
  steps,
  active,
  setActive,
  style
}: {
  steps: SkillsEmbed['steps']
  active: number
  setActive?: (index: number) => void
  style?: React.CSSProperties
}) {
  const step = steps[active]
  const onPrevPoint = React.useCallback(() => {
    if (setActive) {
      setActive(Math.max(0, active - 1))
    }
  }, [active, setActive])
  const onNextPoint = React.useCallback(() => {
    if (setActive) {
      setActive(Math.min(steps.length - 1, active + 1))
    }
  }, [active, steps, setActive])
  const maxSkills = Math.max(...steps.map((step) => step?.skills.length || 0))
  return (
    <div className="poe-SkillGems" style={style}>
      <div className="poe-body">
        {steps.length > 1 && <div className="poe-step-title">{step?.name || ''}</div>}
        <div className="poe-skills">
          {!!step &&
            step.skills.map((skill, index) => (
              <SkillSlot key={index} type={skill.type} item={skill.item} gems={skill.gems} unlink={skill.unlink} />
            ))}
          {[...Array(maxSkills - (step?.skills.length || 0))].map((_, i) => (
            <div className="poe-skill-slot" key={i} />
          ))}
        </div>
      </div>
      {steps.length > 1 && setActive != null && (
        <div className="poe-progression">
          <span
            className={classNames('poe-button poe-minus', {
              'poe-disabled': active <= 0
            })}
            onClick={onPrevPoint}
          />
          <Slider min={0} max={steps.length - 1} step={1} value={active} onChange={setActive} className="ui-Slider" />
          <span
            className={classNames('poe-button poe-plus', {
              'poe-disabled': active >= steps.length - 1
            })}
            onClick={onNextPoint}
          />
        </div>
      )}
    </div>
  )
}

export function SkillGemsEmbed({ embed, ...props }: { embed: SkillsEmbed; style?: React.CSSProperties }) {
  const [active, setActive] = React.useState(embed.step ?? embed.steps.length - 1)
  return <SkillGems steps={embed.steps} active={active} setActive={setActive} {...props} />
}
